import Image from "next/image";
import { useState, useEffect, useRef, memo, startTransition, Component } from "react";
import Link from "next/link";
import HeroArrow1 from "@/images/hero-arrow-1.svg";
import HeroArrow2 from "@/images/hero-arrow-2.svg";
import University from "@/images/hero-university.png";
import Cap from "@/images/hero-cap.png";
import Bag from "@/images/hero-bag.png";
import TIFLogo from "@/images/tif-white.svg";
import NEQSOLLogo from "@/images/neqsol-white.svg";
import WorldPlanet from "@/images/World_Planet.png";
import { useTranslation } from "next-i18next";
import { useWindowSize } from "@/utils/useWindowSize";
import { trackApplyButton } from "@/utils/dataLayer";
import useShouldUseStaticFallback from "@/utils/useHardwareWebGL";
import dynamic from "next/dynamic";

const Spline = dynamic(() => import("@splinetool/react-spline"), {
  ssr: false,
  loading: () => null,
});

/** Catches WebGL/Spline load failures on problematic GPUs and triggers static fallback */
class SplineErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Image
            src={WorldPlanet}
            alt="World Planet"
            className="w-full md:w-auto h-full md:h-auto object-contain"
            fetchPriority="high"
          />
        </div>
      );
    }
    return this.props.children;
  }
}

const Hero = () => {
  const { t } = useTranslation();
  const { width, height: windowHeight } = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);
  const [webGLSupported, setWebGLSupported] = useState(true);
  const [splineFailed, setSplineFailed] = useState(false);
  const useStaticFallback = useShouldUseStaticFallback();

  useEffect(() => {
    if (width !== undefined) {
      startTransition(() => {
        setIsMobile(width < 768);
      });
    }
  }, [width]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasWebGL = !!(
      window.WebGLRenderingContext &&
      (document.createElement("canvas").getContext("webgl") ||
        document.createElement("canvas").getContext("experimental-webgl"))
    );
    setWebGLSupported(hasWebGL);
  }, []);

  const showStaticImage = isMobile || !webGLSupported || useStaticFallback || splineFailed;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);
  const SLIDE_DURATION = 5000;

  const bubbles = [
    { icon: University, text: t("home-page.hero-slide-1-bubble-1") },
    { icon: Cap, text: t("home-page.hero-slide-1-bubble-2") },
    { icon: Bag, text: t("home-page.hero-slide-1-bubble-3") },
  ];

  const slides = [
    {
      title: t("home-page.hero-slide-1-title"),
      subtitle: t("home-page.hero-slide-1-subtitle"),
    },
    {
      title: t("home-page.hero-slide-2-title"),
      subtitle: t("home-page.hero-slide-2-subtitle"),
    },
    {
      title: t("home-page.hero-slide-3-title"),
      subtitle: t("home-page.hero-slide-3-subtitle"),
    },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      timerRef.current = setInterval(() => {
        startTransition(() => {
          setIsTransitioning(true);
          setTimeout(() => {
            startTransition(() => {
              setCurrentSlide((prev) => (prev + 1) % slides.length);
              setIsTransitioning(false);
            });
          }, 300);
        });
      }, SLIDE_DURATION);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleDotClick = (index) => {
    if (index === currentSlide) return;

    startTransition(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        startTransition(() => {
          setCurrentSlide(index);
          setIsTransitioning(false);
        });
      }, 300);
    });
  };

  const currentSlideData = slides[currentSlide];
  const transitionClass = isTransitioning ? "opacity-0" : "opacity-100";

  const getDynamicHeight = () => {
    if (!windowHeight) return 'min-h-[105vh] md:min-h-[90vh] max-h-[105vh] md:max-h-[90vh]';
    if (windowHeight < 700) return 'min-h-[170vh] md:min-h-[110vh] max-h-[150vh] md:max-h-[110vh]';
    if (windowHeight < 800) return 'min-h-[125vh] md:min-h-[100vh] max-h-[125vh] md:max-h-[100vh]';
    return 'min-h-[105vh] md:min-h-[90vh] max-h-[105vh] md:max-h-[90vh]';
  };

  const bubblePositions = [
    `top-[-5%] xs:top-[-10%] sm:top-[-5%] md:top-[-4%] lg:top-[-4%] xl:top-[-4%] 2xl:top-[-4%]
     left-[18%] xs:left-[18%] sm:left-[25%] md:left-[16%] lg:left-[16%] xl:left-[16%] 2xl:left-[16%]`,
    `top-[25%] xs:top-[20%] sm:top-[25%] md:top-[40%] lg:top-[40%] xl:top-[40%] 2xl:top-[40%]
     left-[8%] xs:left-[12%] sm:left-[17%] md:left-[-5%] lg:left-[-4%] xl:left-[0%] 2xl:left-[4%]`,
    `top-[55%] xs:top-[50%] sm:top-[55%] md:top-[83%] lg:top-[83%] xl:top-[83%] 2xl:top-[83%]
     left-[22%] xs:left-[18%] sm:left-[30%] md:left-[10%] lg:left-[36%] xl:left-[33%] 2xl:left-[29%]`,
  ];

  const arrow1Position = `top-[-15%] xs:top-[-20%] sm:top-[-15%] md:top-[0%] lg:top-[0%] xl:top-[0%] 2xl:top-[0%]
                          left-[-18%] xs:left-[-8%] sm:left-[3%] md:left-[-13%] lg:left-[-9%] xl:left-[-4%] 2xl:left-[0%]`;

  const arrow2Position = `top-[22%] xs:top-[15%] sm:top-[22%] md:top-[48%] lg:top-[50%] xl:top-[50%] 2xl:top-[50%]
                          left-[-13%] xs:left-[-10%] sm:left-[10%] md:left-[-25%] lg:left-[12%] xl:left-[14%] 2xl:left-[14%]`;

  return (
    <div className={`relative z-10 flex flex-col md:flex-row bg-[linear-gradient(180deg,#091F59_0%,#1A3884_100%)] bg-cover bg-right bg-no-repeat ${getDynamicHeight()} pt-[112px] mt-[-80px] lg:mt-[-120px] rounded-[24px] lg:rounded-[64px] mx-[8px] mb-[20px] overflow-hidden`}>
      {/* Spline/Image Section */}
      <div className="absolute right-[-40%] sm:right-[-30%] top-[25%] md:right-[-25%] md:top-[10%] lg:right-[-30%] xl:right-[-33%] bottom-0 scale-75 md:scale-100 xl:scale-125 z-0 opacity-80 w-[140%] sm:w-[115%] md:w-full h-full pointer-events-none">
        { showStaticImage ? (
          <div className="w-full h-full flex items-center justify-center">
            <Image
              src={WorldPlanet}
              alt="World Planet"
              className="w-full md:w-auto h-full md:h-auto object-contain"
              fetchPriority="high"
            />
          </div>
        ) : (
          <SplineErrorBoundary onError={() => setSplineFailed(true)}>
            <Spline
              scene="https://prod.spline.design/Yo5EjcsihpAesWL1/scene.splinecode"
              className="w-full h-full"
            />
          </SplineErrorBoundary>
        )}
      </div>

      <div className="flex flex-col md:flex-row w-full max-w-[90%] mx-auto gap-[20px] relative z-10">
        <div className="flex flex-col flex-1 z-10 pb-[10px] md:pb-0 items-start justify-center">
          <div className="flex flex-col h-[320px] sm:h-[350px] md:h-[450px] pr-[20px]">
            <div className="flex md:hidden items-center gap-[10px] mb-[20px] sm:mb-[56px]">
              <Image src={TIFLogo} alt="TIF Logo" className="w-auto h-[32px]" />
              <div className="h-[32px] w-[1px] bg-white/20 mx-[10px]" />
              <Image src={NEQSOLLogo} alt="NEQSOL Logo" className="w-auto h-[32px]" />
            </div>
            <div className="flex-1 flex flex-col gap-[15px] xs:gap-[20px] sm:gap-[30px] min-h-0">
              <h1 className={`sm:text-[40px] xs:text-[32px] text-[24px] font-[700] leading-[120%] lg:text-[48px] text-white max-w-[600px] transition-opacity duration-300 ${transitionClass}`}>
                {currentSlideData.title} 
              </h1>

              <p className={`text-[12px] xs:text-[14px] lg:text-[16px] text-gray-300 w-full transition-opacity duration-300 ${transitionClass}`}>
                {currentSlideData.subtitle}
              </p>
            </div>

            <div className="flex flex-col gap-[15px] xs:gap-[20px] sm:gap-[30px] shrink-0">
              <div className="flex items-center gap-[8px]">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`transition-all duration-300 rounded-full flex items-center justify-center ${index === currentSlide
                      ? "w-[16px] h-[16px] border border-white"
                      : "w-[16px] h-[16px] border border-white/20 bg-transparent hover:border-gray-300"
                      }`}
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <div className={`w-[8px] h-[8px] rounded-full ${index === currentSlide ? "bg-white" : "bg-white/20"}`} />
                  </button>
                ))}
              </div>

              <Link href="/apply" onClick={() => trackApplyButton("hero")}>
                <button className="flex h-[48px] w-[220px] items-center justify-center gap-[10px] rounded-full bg-main-pink border border-main-pink transition duration-300 text-white hover:text-main-pink hover:bg-white">
                  <p className="text-[14px] font-[400] sm:text-[18px]">{t("join-us")}</p>
                </button>
              </Link>

              <div className="hidden md:flex items-center gap-[10px]">
                <Image src={TIFLogo} alt="TIF Logo" className="w-auto h-[38px]" />
                <div className="h-[38px] w-[1px] bg-white/20 mx-[10px]" />
                <Image src={NEQSOLLogo} alt="NEQSOL Logo" className="w-auto h-[38px]" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 relative flex items-center justify-center md:justify-end mt-[40px] md:mt-0">
          <div className="relative w-full h-[500px] md:h-[600px] my-auto">
            <div className={`absolute transform scale-50 md:scale-100 ${arrow1Position}`}>
              <Image src={HeroArrow1} alt="Arrow" className="w-auto" priority />
            </div>

            <div className={`absolute transform scale-50 md:scale-100 ${arrow2Position}`}>
              <Image src={HeroArrow2} alt="Arrow" className="w-auto" priority />
            </div>

            {bubbles.map((bubble, index) => (
              <div key={index} className={`absolute transform transition-opacity duration-300 ${bubblePositions[index]}`}>
                <div className="group flex items-center gap-[12px] bg-[#091F59]/70 backdrop-blur-sm rounded-[8px] md:rounded-full px-[16px] sm:px-[20px] shadow-lg border border-white/30 min-w-[205px] max-w-[205px] md:min-w-[295px] md:max-w-[295px] h-[48px] md:h-[60px] lg:h-[72px] hover:bg-white hover:border-white/40 transition-colors duration-300 cursor-pointer">
                  <Image src={bubble.icon} alt={bubble.text} className="w-auto h-[24px] md:h-[32px] lg:h-[40px]" loading="lazy" />
                  <p className="text-white group-hover:text-[#091F59] text-[12px] md:text-[14px] lg:text-[16px] font-[500] leading-[140%] w-full transition-colors duration-300">
                    {bubble.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Hero);
